import type { ReactNode } from 'react'
import { useT } from '../../i18n/LocaleContext'
import { OptionCard, RangeSlider } from './fields'
import { SECTORS, SOURCINGS, type DevisAnswers, type Sector } from './types'

const IP = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const SECTOR_ICONS: Record<Sector, ReactNode> = {
  mode: (
    <svg {...IP}>
      <path d="M12 4l-2 2c0 1 1 2 2 2s2-1 2-2L12 4z" />
      <path d="M12 8L4 14l3 1 5-3 5 3 3-1-8-6z" />
      <path d="M7 15v4h10v-4" />
    </svg>
  ),
  cosmetique: (
    <svg {...IP}>
      <rect x="9" y="3" width="6" height="3" rx="1" />
      <path d="M8 6h8l-1 4H9z" />
      <rect x="8" y="10" width="8" height="11" rx="2" />
    </svg>
  ),
  parfumerie: (
    <svg {...IP}>
      <rect x="9" y="3" width="6" height="3" />
      <path d="M8 6h8v2H8z" />
      <rect x="7" y="8" width="10" height="13" rx="2" />
      <path d="M17 11l3-1M17 13l3 1" />
    </svg>
  ),
  maison: (
    <svg {...IP}>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-5h4v5" />
    </svg>
  ),
  jardin: (
    <svg {...IP}>
      <path d="M12 21v-7" />
      <path d="M12 14c-3 0-6-3-6-7 4 0 6 3 6 7z" />
      <path d="M12 14c3 0 6-3 6-7-4 0-6 3-6 7z" />
    </svg>
  ),
  electronique: (
    <svg {...IP}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M9 6V3M15 6V3M9 21v-3M15 21v-3M3 9h3M3 15h3M18 9h3M18 15h3" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  ),
  food: (
    <svg {...IP}>
      <rect x="3" y="9" width="18" height="6" rx="3" />
      <path d="M12 9v6" />
    </svg>
  ),
  autre: (
    <svg {...IP}>
      <path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
      <path d="M3 8l9 5 9-5M12 13v8" />
    </svg>
  ),
}

export default function StepProducts({
  answers,
  update,
}: {
  answers: DevisAnswers
  update: (patch: Partial<DevisAnswers>) => void
}) {
  const { t } = useT()
  return (
    <div className="space-y-8">
      <h3 className="text-lg font-semibold tracking-tight text-ink-950">{t('devisWizard.step1.title')}</h3>

      <div>
        <p className="mb-3 text-xs font-semibold text-ink-800">{t('devisWizard.step1.sectorLabel')}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {SECTORS.map((s) => (
            <OptionCard
              key={s}
              label={t('devisWizard.sectors.' + s)}
              icon={SECTOR_ICONS[s]}
              selected={answers.sector === s}
              onClick={() => update({ sector: s })}
            />
          ))}
        </div>
      </div>

      <div>
        <p className="mb-3 text-xs font-semibold text-ink-800">{t('devisWizard.step1.sourcingLabel')}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {SOURCINGS.map((s) => (
            <OptionCard
              key={s}
              label={t('devisWizard.sourcing.' + s)}
              selected={answers.sourcing === s}
              onClick={() => update({ sourcing: s })}
            />
          ))}
        </div>
      </div>

      <RangeSlider
        label={t('devisWizard.step1.basketLabel')}
        min={5}
        max={500}
        step={5}
        value={answers.avgBasket}
        onChange={(v) => update({ avgBasket: v })}
        format={(v, atMax) => (
          <>
            {v}
            {atMax ? '+' : ''} <span className="text-base font-medium text-ink-500">{t('devisWizard.units.basket')}</span>
          </>
        )}
      />
    </div>
  )
}
